/**
 * Config Store
 * 앱 설정 관리. 지원 언어 목록은 DB(SUPPORTED_LANGUAGES) 시드를 우선 사용하고,
 * API 호출 실패 시 SPA 가 실제 가진 i18n 번들 목록으로 fallback.
 *
 * DB code('zh-TW','zh-CN' 등) ↔ SPA locale key('zhTW','zhCN' 등) 변환은
 * dbCodeToLocaleKey 한 곳에서 정규화.
 *
 * SUPPORTED_BUNDLES 화이트리스트: DB 에 신규 언어를 추가해도 SPA 가 해당
 * locale bundle 을 가지고 있지 않으면 dropdown 에 노출되지 않게 차단.
 * (i18n 메시지 없이 노출되면 영문 또는 키 raw 가 표시되는 UX 깨짐 방지)
 *
 * localStorage 키는 LOCALE_STORAGE_KEY 한 상수로 통일 (이전 'site-locale'
 * 잔존 키도 1회 마이그레이션).
 */
import { defineStore } from 'pinia';
import { systemApi } from '@/api/system';

const LOCALE_STORAGE_KEY = 'locale';
const LEGACY_LOCALE_STORAGE_KEY = 'site-locale';

// SPA i18n 번들이 실제 존재하는 locale 키. main.js 의 createI18n messages 와 동기화 필수.
// DB 에 다른 언어 추가 시 SPA bundle 도 같이 추가하고 이 배열도 갱신해야 노출됨.
const SUPPORTED_BUNDLES = ['ko', 'en', 'ja', 'zhTW', 'zhCN'];

// DB 코드(예: 'zh-TW') → SPA locale 키(예: 'zhTW') 변환.
//   - 'ko' → 'ko'
//   - 'zh-TW' → 'zhTW' (하이픈 제거 + 끝부분 대문자 유지)
//   - 'zh-CN' → 'zhCN'
//   - 향후 'zh-HK' → 'zhHK' 도 자동 지원
//   - 비정상 입력은 null 반환
function dbCodeToLocaleKey(dbCode) {
    if (!dbCode) return null;
    const trimmed = String(dbCode).trim();
    if (!trimmed) return null;
    const parts = trimmed.split('-').filter(p => p.length > 0);
    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0].toLowerCase();
    return parts[0].toLowerCase() + parts.slice(1).map(p => p.toUpperCase()).join('');
}

export const useConfigStore = defineStore('config', {
    state: () => ({
        // Fallback 하드코딩 목록 (API 실패 시 또는 초기 로드 전에 사용)
        // SUPPORTED_BUNDLES 와 같은 키 집합을 유지해야 함.
        langList: {
            ko:   { key: 'korean',      langName: '한국어',   flag: 'SouthKorea.png' },
            en:   { key: 'english',     langName: 'English',  flag: 'USA.png' },
            ja:   { key: 'japanese',    langName: '日本語',   flag: 'Japan.png' },
            zhTW: { key: 'traditional', langName: '繁體中文', flag: 'Taiwan.png' },
            zhCN: { key: 'simplified',  langName: '简体中文', flag: 'China.png' },
        },
        fallbackLang: 'en',
        apiServer: '/api/',
        currentLang: 'ko',

        // DB SUPPORTED_LANGUAGES 시드 결과 (raw)
        supportedLanguages: [],
        languagesLoaded: false,
        // 중복 호출 가드용 진행중 Promise. 두 컴포넌트가 동시에 onMounted 트리거 시
        // 둘 다 같은 Promise 를 await — 두 번째 호출자도 실제 완료를 기다림.
        languagesLoadingPromise: null,
    }),

    getters: {
        /**
         * 사용 가능한 언어 목록.
         *   - DB 시드가 채워있으면 그것을 사용 (use_yn='Y' + seq 정렬)
         *   - SPA 가 i18n bundle 을 가진 항목만 노출 (SUPPORTED_BUNDLES 교집합)
         *   - DB 비어있거나 미로드면 fallback 5개 (SUPPORTED_BUNDLES 전체)
         *   - DB 성공이지만 결과 빈 경우 빈 배열 (운영자 의도 존중)
         */
        availableLanguages: (state) => {
            if (state.languagesLoaded && state.supportedLanguages.length > 0) {
                return state.supportedLanguages
                    .filter(item => (item.useYn || 'Y') === 'Y')
                    .map(item => ({
                        item,
                        code: dbCodeToLocaleKey(item.codeCd),
                    }))
                    .filter(({ code }) => code && SUPPORTED_BUNDLES.includes(code))
                    .sort((a, b) => (a.item.seq || 0) - (b.item.seq || 0))
                    .map(({ item, code }) => {
                        const fallback = state.langList[code] || {};
                        return {
                            code,
                            key: fallback.key || (item.codeCd || code).toLowerCase(),
                            langName: item.codeName || fallback.langName || code,
                            flag: item.val1 || fallback.flag || null,
                        };
                    });
            }
            if (state.languagesLoaded && state.supportedLanguages.length === 0) {
                // 운영자가 의도적으로 모두 비활성화한 결과 — 그대로 존중 (빈 배열)
                return [];
            }
            // 미로드 또는 API 실패 — fallback 5개
            return SUPPORTED_BUNDLES
                .filter(code => state.langList[code])
                .map(code => ({ code, ...state.langList[code] }));
        },
    },

    actions: {
        /**
         * 지원 언어 목록 DB 로드.
         *   - 캐시 있으면 즉시 반환 (force=true 시 강제 갱신)
         *   - 진행 중인 Promise 가 있으면 그것을 반환 → 모든 호출자 같은 결과 await
         *   - 실패 시 fallback 그대로 사용 (예외 던지지 않음)
         *   - DB 항목을 langList 객체에도 머지 — 기존 langList[code] 직접 접근 코드 호환
         */
        async loadSupportedLanguages(force = false) {
            if (this.languagesLoaded && !force) return;
            if (this.languagesLoadingPromise) {
                await this.languagesLoadingPromise;
                return;
            }
            this.languagesLoadingPromise = (async () => {
                try {
                    const resp = await systemApi.getSupportedLanguages();
                    if (resp?.data?.result && Array.isArray(resp.data.list)) {
                        this.supportedLanguages = resp.data.list;
                        // langList 에도 머지 (기존 langList[code] 직접 접근 호환)
                        // SUPPORTED_BUNDLES 안에 있는 항목만 머지.
                        resp.data.list.forEach(item => {
                            const code = dbCodeToLocaleKey(item.codeCd);
                            if (code && SUPPORTED_BUNDLES.includes(code) && !this.langList[code]) {
                                this.langList[code] = {
                                    key: (item.codeCd || code).toLowerCase(),
                                    langName: item.codeName || code,
                                    flag: item.val1 || null,
                                };
                            }
                        });
                        this.languagesLoaded = true;
                    }
                } catch (e) {
                    console.warn('[config] loadSupportedLanguages failed, using fallback', e);
                } finally {
                    this.languagesLoadingPromise = null;
                }
            })();
            await this.languagesLoadingPromise;
        },

        /**
         * 활성 지원 목록에 있는 코드만 수락.
         * DB 비활성화 후에도 저장된 localStorage 값으로 비활성 언어가 적용되는 우회 방지.
         */
        setLanguage(langCode) {
            const allowed = this.availableLanguages.some(l => l.code === langCode);
            if (allowed) {
                this.currentLang = langCode;
                try {
                    localStorage.setItem(LOCALE_STORAGE_KEY, langCode);
                } catch { /* localStorage 미허용 환경 무시 */ }
            }
        },

        /**
         * 저장된 locale 읽기 + legacy 'site-locale' 키 1회 마이그레이션.
         * 외부(LangSelector 등) 에서 호출해 legacy 제거가 실제로 일어나도록 함.
         */
        readSavedLocale() {
            try {
                const current = localStorage.getItem(LOCALE_STORAGE_KEY);
                if (current) return current;
                const legacy = localStorage.getItem(LEGACY_LOCALE_STORAGE_KEY);
                if (legacy) {
                    localStorage.setItem(LOCALE_STORAGE_KEY, legacy);
                    localStorage.removeItem(LEGACY_LOCALE_STORAGE_KEY);
                    return legacy;
                }
            } catch { /* localStorage 미허용 환경 무시 */ }
            return null;
        },

        /**
         * 활성 언어 목록에 포함된 코드만 currentLang 으로 설정.
         * DB 비활성화된 언어가 저장값 우회로 적용되는 문제 방지.
         */
        initLanguage() {
            const saved = this.readSavedLocale();
            if (saved && this.availableLanguages.some(l => l.code === saved)) {
                this.currentLang = saved;
            }
        },
    },
});

// 외부에서도 같은 키 사용할 수 있도록 export (LangSelector 등)
export { LOCALE_STORAGE_KEY, LEGACY_LOCALE_STORAGE_KEY, SUPPORTED_BUNDLES, dbCodeToLocaleKey };
